import React, { useState, useEffect, useCallback } from "react";
import { Form } from "react-bootstrap";
import {
  GetCustomers,
  getCustomerById,
  UpdateCustomer,
} from "../database/userInfo";
import { getTranslations } from "../Shared/translations";
import KlantComp from "././klant";
import "./eindklantgegevens.css";
import "./shared.css";

interface Customer {
  id: string;
  user_id?: string;
  voornaam: string;
  naam: string;
  firma: string;
  straat: string;
  huisnummer: string;
  postcode: string;
  gemeente: string;
  land: string;
  telefoon: string;
  btw: string;
}

interface CustomerText {
  id: any;
  displayText: string;
}

const EindkKlantgegevens = () => {
  const [klant, setKlant] = useState<Customer>({
    id: "",
    naam: "",
    voornaam: "",
    firma: "",
    straat: "",
    huisnummer: "",
    postcode: "",
    gemeente: "",
    land: "",
    telefoon: "",
    btw: "",
  });
  const t = getTranslations();
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [getCustomersCmd, setGetCustomersCmd] = useState(true);

  const [klantId, setKlantId] = useState(() => {
    return sessionStorage.getItem("eindklantId") || "0";
  });

  const getCustomers = useCallback(async () => {
    try {
      const data = await GetCustomers();
      const userInfo = data.success;

      const klantArray: Customer[] = userInfo.map((customer: any) => ({
        id: customer.id,
        user_id: customer.user_id,
        voornaam: customer.voornaam,
        naam: customer.naam,
        firma: customer.firma,
        straat: customer.straat,
        huisnummer: customer.huisnummer,
        postcode: customer.postcode,
        gemeente: customer.gemeente,
        land: customer.land,
        telefoon: customer.telefoon,
        btw: customer.btw,
      }));

      if (getCustomersCmd || customers.length !== klantArray.length) {
        setCustomers(klantArray);
        setGetCustomersCmd(false);
      }
    } catch (error) {
      console.error("Error fetching customers:", error);
    }
  }, [customers.length, getCustomersCmd]);

  const getCustomersById = async (id: string) => {
    const data = await getCustomerById(parseInt(id));
    const klant = data.success[0];

    setKlant({
      id: klant.id,
      naam: klant.naam,
      voornaam: klant.voornaam,
      firma: klant.firma,
      straat: klant.straat,
      huisnummer: klant.huisnummer,
      postcode: klant.postcode,
      gemeente: klant.gemeente,
      land: klant.land,
      telefoon: klant.telefoon,
      btw: klant.btw,
    });
    return data;
  };

  const saveNew = async (
    id: any,
    naam: string,
    voornaam: string,
    firma: string,
    straat: string,
    huisnr: string,
    postcode: string,
    gemeente: string,
    land: string,
    telefoon: string,
    btwnr: string
  ) => {
    try {
      const created = await UpdateCustomer(
        id,
        naam,
        voornaam,
        firma,
        straat,
        huisnr,
        postcode,
        gemeente,
        land,
        telefoon,
        btwnr
      );

      const data = created?.data?.data;
      const newId = data?.id;

      if (newId) {
        sessionStorage.setItem("eindklantId", newId);
        setKlant(data);
        sessionStorage.setItem("eindklant", `${data.naam} ${data.voornaam}`);
        sessionStorage.setItem("eindklantId", data.id);
      }

      setGetCustomersCmd(true);
    } catch (error) {
      console.error("Fout bij opslaan van klant:", error);
    }
  };

  const dropdownOptions: CustomerText[] = [
    { id: 0, displayText: t.maakKlant },
    ...customers.map((offerteItem) => {
      let displayText = "";
      if (offerteItem.firma) displayText += `${offerteItem.firma} - `;
      if (offerteItem.naam) {
        displayText += offerteItem.naam;
        if (offerteItem.voornaam) displayText += ` ${offerteItem.voornaam}`;
      }
      return { id: offerteItem.id, displayText };
    }),
  ];

  useEffect(() => {
    sessionStorage.setItem("eindklantId", klantId);
  }, [klantId]);

  useEffect(() => {
    if (klantId !== "0" && klantId !== "") {
      getCustomersById(klantId);
    }
  }, [klant.id, klantId]);

  useEffect(() => {
    if (getCustomersCmd) {
      getCustomers();
      setGetCustomersCmd(false);
    }
  }, [getCustomersCmd, getCustomers]);

  const resetForm = () => {
    setKlantId("");
    setKlant({
      id: "",
      naam: "",
      voornaam: "",
      firma: "",
      straat: "",
      huisnummer: "",
      postcode: "",
      gemeente: "",
      land: "",
      telefoon: "",
      btw: "",
    });
  };

  const handleCustomerChange = async (
    event: React.ChangeEvent<HTMLSelectElement>
  ) => {
    const selectedId = event.target.value;

    setKlantId(selectedId);
    sessionStorage.setItem("eindklantId", selectedId);
    sessionStorage.setItem("eindklant", `${klant.naam} ${klant.voornaam}`);
    if (selectedId === "0") {
      resetForm();
    } else {
      const cust = await getCustomersById(selectedId);
      if (cust) {
        sessionStorage.setItem("eindklantId", cust.success[0].id);
        sessionStorage.setItem(
          "eindklant",
          `${cust.success[0].naam} ${cust.success[0].voornaam}`
        );
        setKlant(cust.success[0]);
      }
    }
  };

  const klantDropdown = (
    <Form.Select
      aria-label="Selecteer een klant"
      onChange={handleCustomerChange}
      key="KlantenDropdown"
      value={klantId}
    >
      {dropdownOptions.map((offerteItem) => (
        <option value={offerteItem.id} key={offerteItem.id}>
          {offerteItem.displayText}
        </option>
      ))}
    </Form.Select>
  );

  const updateKlant = async (klant: Customer) => {
    await saveNew(
      klant.id,
      klant.naam,
      klant.voornaam,
      klant.firma,
      klant.straat,
      klant.huisnummer,
      klant.postcode,
      klant.gemeente,
      klant.land,
      klant.telefoon,
      klant.btw
    );
    setKlant(klant);
    sessionStorage.setItem("eindklant", `${klant.naam} ${klant.voornaam}`);
  };

  return (
    <>
      {klantDropdown}
      <KlantComp onKlantUpdate={updateKlant} klant={klant} />
    </>
  );
};

export default EindkKlantgegevens;
